/**
 * How the platform earns from an apartment, in words.
 *
 * The label was a hardcoded map repeated in four files. It is one string here
 * because it now takes the owner's name: an owner proposing terms for their own
 * building reads "CoLiviq collects rent, wires net to Pascal Fehst", not "…to
 * owner", which in a form they are filling in about themselves is a strange
 * way to be addressed.
 *
 * `platformName` exists for operator-portal users (COL-352): an operator's
 * surfaces must name their company, not CoLiviq. With no arguments it returns
 * exactly the previous wording, so the staff-facing sheets — where the
 * logged-in user is neither the owner nor an operator — are unchanged.
 */
import type { CollectionMode } from "@/types/property";

/** Translator shape from `useLanguage()`, kept local so this file stays pure. */
type Translate = (key: string, fallback?: string) => string;

/**
 * Pass `t` from `useLanguage()` to get a translated label; omitted, this
 * returns the plain English form (used by tests and any non-UI caller).
 * `platformName` ("CoLiviq" or an operator's own name) is a proper noun and
 * is substituted in after translation, never itself translated.
 */
export function collectionModeLabels(
  ownerName?: string | null,
  platformName: string = "coliviq",
  t?: Translate
): Record<CollectionMode, string> {
  const owner = ownerName?.trim();
  const tr = (key: string, fallback: string) =>
    t ? t(key, fallback) : fallback;
  return {
    company_collects: owner
      ? tr(
          "admin.collectionMode.companyCollectsNamed",
          "{platform} collects rent, wires net to {owner}"
        )
          .replace("{platform}", platformName)
          .replace("{owner}", owner)
      : tr(
          "admin.collectionMode.companyCollects",
          "{platform} collects rent, wires net to owner"
        ).replace("{platform}", platformName),
    owner_collects: owner
      ? tr(
          "admin.collectionMode.ownerCollectsNamed",
          "{owner} collects rent, pays {platform} the fee"
        )
          .replace("{owner}", owner)
          .replace("{platform}", platformName)
      : tr(
          "admin.collectionMode.ownerCollects",
          "Owner collects rent, pays {platform} the fee"
        ).replace("{platform}", platformName),
  };
}
